"use client";

import { useLinkMetadata } from "@/hooks/useLinkMetadata";
import { firstURL } from "@/lib/urlDetector";

interface LinkPreviewCardProps {
  text: string | null | undefined;
  compact?: boolean;
}

/**
 * Detects the first URL in text and renders an OG link preview card.
 * Three states: rich preview (image + title + site), loading, fallback (plain link).
 * Mirrors iOS LinkPreviewCard.swift behavior.
 */
export function LinkPreviewCard({ text, compact = false }: LinkPreviewCardProps) {
  const url = firstURL(text);
  const { metadata, loading } = useLinkMetadata(url);

  if (!url) return null;

  let hostname = "";
  try {
    hostname = new URL(url).hostname;
  } catch {
    hostname = url;
  }

  // Loading state
  if (loading && !metadata) {
    return (
      <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-white/5 mt-2">
        <div
          className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin shrink-0"
          style={{
            borderColor: "var(--accent-color, #00C7BE)",
            borderTopColor: "transparent",
          }}
        />
        <span className="text-xs text-gray-400 truncate">{hostname}</span>
      </div>
    );
  }

  // Rich preview (has title or image)
  if (metadata && (metadata.title || metadata.imageUrl)) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="block mt-2 rounded-xl bg-gray-50 dark:bg-white/5 overflow-hidden hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
      >
        <div className={`flex ${compact ? "gap-2 p-2" : "gap-3 p-3"}`}>
          {metadata.imageUrl && (
            <img
              src={metadata.imageUrl}
              alt=""
              className={`object-cover rounded-lg shrink-0 ${
                compact ? "w-12 h-12" : "w-16 h-16"
              }`}
              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
            />
          )}
          <div className="flex-1 min-w-0">
            {!compact && metadata.siteName && (
              <p className="text-[11px] text-gray-400 truncate">
                {metadata.siteName}
              </p>
            )}
            {metadata.title && (
              <p className={`font-semibold text-gray-800 dark:text-gray-200 ${
                compact ? "text-xs line-clamp-1" : "text-sm line-clamp-2"
              }`}>
                {metadata.title}
              </p>
            )}
            {!compact && metadata.description && (
              <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">
                {metadata.description}
              </p>
            )}
            {!compact && (
              <p className="text-[11px] text-gray-400 truncate mt-0.5">
                {hostname}
              </p>
            )}
          </div>
        </div>
      </a>
    );
  }

  // Fallback — plain link
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 p-3 rounded-xl bg-gray-50 dark:bg-white/5 mt-2 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-blue-500 shrink-0"
      >
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </svg>
      <span className="text-xs text-blue-500 truncate">{url}</span>
    </a>
  );
}
